<template>
    <div class="telephone-input-wrapper" ref="wrapperRef">
        <div class="flex items-stretch">
            <!-- Country Code Dropdown -->
            <div class="relative flex-shrink-0" ref="dropdownRef">
                <button type="button" @click="toggleDropdown" class="survey-tel-dropdown"
                    :class="{ 'border-b47c2c': isDropdownOpen }">
                    <span class="flex items-center space-x-2">
                        <span class="text-lg">{{ selectedCountryFlag }}</span>
                        <span class="text-sm font-medium text-gray-700">{{ selectedCountryCode }}</span>
                        <font-awesome-icon icon="chevron-down" class="text-xs text-gray-500 ml-1" />
                    </span>
                </button>
                <!-- Dropdown Menu -->
                <div v-if="isDropdownOpen" ref="dropdownMenuRef" class="telephone-dropdown-menu"
                    :class="dropdownPositionClass" :style="dropdownStyle">
                    <div class="p-2" @mousedown.prevent>
                        <input ref="searchInputRef" v-model="searchQuery" type="text" placeholder="Search country..."
                            class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-b47c2c focus:border-b47c2c"
                            @mousedown.stop @click.stop @focus.stop />
                    </div>
                    <div class="max-h-64 overflow-y-auto">
                        <button v-for="country in filteredCountries" :key="country.code" type="button"
                            @click="selectCountry(country)"
                            class="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center space-x-3 transition-colors"
                            :class="{ 'bg-gray-50': selectedCountryCode === country.code }">
                            <span class="text-lg flex-shrink-0">{{ country.flag }}</span>
                            <span class="flex-1 text-sm text-gray-700 truncate">{{ country.name }}</span>
                            <span class="text-sm text-gray-500 flex-shrink-0">{{ country.code }}</span>
                        </button>
                    </div>
                </div>
            </div>
            <!-- Phone Number Input -->
            <input :id="inputId" v-model="phoneNumber" type="tel" :placeholder="placeholder" :required="required"
                class="survey-tel-input-field" @input="onInput" @focus="onFocus" />
        </div>
    </div>
</template>

<script>
import { getCountryFlag } from '@/helpers/countryFlags';

// Country codes mapping (common countries with their codes and names)
const COUNTRY_CODES = [
    { code: '+971', name: 'United Arab Emirates', flagKey: 'UAE' },
    { code: '+966', name: 'Saudi Arabia', flagKey: 'Saudi Arabia' },
    { code: '+965', name: 'Kuwait', flagKey: 'Kuwait' },
    { code: '+974', name: 'Qatar', flagKey: 'Qatar' },
    { code: '+973', name: 'Bahrain', flagKey: 'Bahrain' },
    { code: '+968', name: 'Oman', flagKey: 'Oman' },
    { code: '+1', name: 'United States', flagKey: 'United States' },
    { code: '+44', name: 'United Kingdom', flagKey: 'United Kingdom' },
    { code: '+91', name: 'India', flagKey: 'India' },
    { code: '+92', name: 'Pakistan', flagKey: 'Pakistan' },
    { code: '+880', name: 'Bangladesh', flagKey: 'Bangladesh' },
    { code: '+94', name: 'Sri Lanka', flagKey: 'Sri Lanka' },
    { code: '+977', name: 'Nepal', flagKey: 'Nepal' },
    { code: '+62', name: 'Indonesia', flagKey: 'Indonesia' },
    { code: '+60', name: 'Malaysia', flagKey: 'Malaysia' },
    { code: '+66', name: 'Thailand', flagKey: 'Thailand' },
    { code: '+84', name: 'Vietnam', flagKey: 'Vietnam' },
    { code: '+65', name: 'Singapore', flagKey: 'Singapore' },
    { code: '+63', name: 'Philippines', flagKey: 'Philippines' },
    { code: '+86', name: 'China', flagKey: 'China' },
    { code: '+81', name: 'Japan', flagKey: 'Japan' },
    { code: '+82', name: 'South Korea', flagKey: 'South Korea' },
    { code: '+20', name: 'Egypt', flagKey: 'Egypt' },
    { code: '+212', name: 'Morocco', flagKey: 'Morocco' },
    { code: '+213', name: 'Algeria', flagKey: 'Algeria' },
    { code: '+216', name: 'Tunisia', flagKey: 'Tunisia' },
    { code: '+218', name: 'Libya', flagKey: 'Libya' },
    { code: '+249', name: 'Sudan', flagKey: 'Sudan' },
    { code: '+962', name: 'Jordan', flagKey: 'Jordan' },
    { code: '+961', name: 'Lebanon', flagKey: 'Lebanon' },
    { code: '+963', name: 'Syria', flagKey: 'Syria' },
    { code: '+964', name: 'Iraq', flagKey: 'Iraq' },
    { code: '+98', name: 'Iran', flagKey: 'Iran' },
    { code: '+90', name: 'Turkey', flagKey: 'Turkey' },
    { code: '+972', name: 'Israel', flagKey: 'Israel' },
    { code: '+970', name: 'Palestine', flagKey: 'Palestine' },
    { code: '+27', name: 'South Africa', flagKey: 'South Africa' },
    { code: '+234', name: 'Nigeria', flagKey: 'Nigeria' },
    { code: '+254', name: 'Kenya', flagKey: 'Kenya' },
    { code: '+233', name: 'Ghana', flagKey: 'Ghana' },
    { code: '+251', name: 'Ethiopia', flagKey: 'Ethiopia' },
    { code: '+33', name: 'France', flagKey: 'France' },
    { code: '+49', name: 'Germany', flagKey: 'Germany' },
    { code: '+39', name: 'Italy', flagKey: 'Italy' },
    { code: '+34', name: 'Spain', flagKey: 'Spain' },
    { code: '+31', name: 'Netherlands', flagKey: 'Netherlands' },
    { code: '+32', name: 'Belgium', flagKey: 'Belgium' },
    { code: '+41', name: 'Switzerland', flagKey: 'Switzerland' },
    { code: '+43', name: 'Austria', flagKey: 'Austria' },
    { code: '+46', name: 'Sweden', flagKey: 'Sweden' },
    { code: '+47', name: 'Norway', flagKey: 'Norway' },
    { code: '+45', name: 'Denmark', flagKey: 'Denmark' },
    { code: '+358', name: 'Finland', flagKey: 'Finland' },
    { code: '+61', name: 'Australia', flagKey: 'Australia' },
    { code: '+64', name: 'New Zealand', flagKey: 'New Zealand' },
    { code: '+55', name: 'Brazil', flagKey: 'Brazil' },
    { code: '+54', name: 'Argentina', flagKey: 'Argentina' },
    { code: '+56', name: 'Chile', flagKey: 'Chile' },
    { code: '+57', name: 'Colombia', flagKey: 'Colombia' },
    { code: '+51', name: 'Peru', flagKey: 'Peru' },
    { code: '+58', name: 'Venezuela', flagKey: 'Venezuela' },
    { code: '+593', name: 'Ecuador', flagKey: 'Ecuador' },
    { code: '+7', name: 'Russia', flagKey: 'Russia' },
    { code: '+380', name: 'Ukraine', flagKey: 'Ukraine' },
    { code: '+48', name: 'Poland', flagKey: 'Poland' },
    { code: '+420', name: 'Czech Republic', flagKey: 'Czech Republic' },
    { code: '+36', name: 'Hungary', flagKey: 'Hungary' },
    { code: '+40', name: 'Romania', flagKey: 'Romania' },
    { code: '+359', name: 'Bulgaria', flagKey: 'Bulgaria' },
    { code: '+30', name: 'Greece', flagKey: 'Greece' },
    { code: '+351', name: 'Portugal', flagKey: 'Portugal' },
    { code: '+353', name: 'Ireland', flagKey: 'Ireland' },
    { code: '+52', name: 'Mexico', flagKey: 'Mexico' },
    { code: '+1', name: 'Canada', flagKey: 'Canada' },
];

export default {
    name: 'TelephoneInput',
    props: {
        modelValue: {
            type: String,
            default: '',
        },
        countryCode: {
            type: String,
            default: '+971',
        },
        placeholder: {
            type: String,
            default: 'Enter your telephone number',
        },
        required: {
            type: Boolean,
            default: false,
        },
        inputId: {
            type: String,
            default: '',
        },
    },
    emits: ['update:modelValue', 'update:countryCode'],
    data() {
        return {
            phoneNumber: this.modelValue || '',
            selectedCountryCode: this.countryCode || '+971',
            isDropdownOpen: false,
            searchQuery: '',
            countries: [],
            dropdownPosition: 'bottom', // 'top' or 'bottom'
            dropdownWidth: 256, // Default width in pixels (w-64 = 16rem = 256px)
        };
    },
    computed: {
        selectedCountryFlag() {
            const country = this.countries.find(c => c.code === this.selectedCountryCode);
            return country ? country.flag : '🇦🇪';
        },
        filteredCountries() {
            if (!this.searchQuery) {
                return this.countries;
            }
            const query = this.searchQuery.toLowerCase();
            return this.countries.filter(country =>
                country.name.toLowerCase().includes(query) ||
                country.code.includes(query)
            );
        },
        dropdownPositionClass() {
            return {
                'dropdown-top': this.dropdownPosition === 'top',
                'dropdown-bottom': this.dropdownPosition === 'bottom',
            };
        },
        dropdownStyle() {
            return {
                width: `${this.dropdownWidth}px`,
                minWidth: '200px',
                maxWidth: '100%',
            };
        },
    },
    watch: {
        modelValue(newVal) {
            this.phoneNumber = newVal || '';
        },
        countryCode(newVal) {
            this.selectedCountryCode = newVal || '+971';
        },
        phoneNumber(newVal) {
            this.$emit('update:modelValue', newVal);
        },
        selectedCountryCode(newVal) {
            this.$emit('update:countryCode', newVal);
        },
    },
    mounted() {
        this.initializeCountries();
        this.setupClickOutside();
        window.addEventListener('resize', this.calculateDropdownPosition);
        window.addEventListener('scroll', this.calculateDropdownPosition, true);
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.calculateDropdownPosition);
        window.removeEventListener('scroll', this.calculateDropdownPosition, true);
        document.removeEventListener('click', this.handleClickOutside);
    },
    methods: {
        initializeCountries() {
            this.countries = COUNTRY_CODES.map(country => ({
                code: country.code,
                name: country.name,
                flag: getCountryFlag(country.flagKey),
            }));
        },
        setupClickOutside() {
            document.addEventListener('click', this.handleClickOutside);
        },
        handleClickOutside(event) {
            if (this.isDropdownOpen && this.$refs.wrapperRef && !this.$refs.wrapperRef.contains(event.target)) {
                this.closeDropdown();
            }
        },
        calculateDropdownPosition() {
            this.$nextTick(() => {
                if (!this.isDropdownOpen || !this.$refs.dropdownRef || !this.$refs.dropdownMenuRef) {
                    return;
                }

                const dropdownButton = this.$refs.dropdownRef;
                const rect = dropdownButton.getBoundingClientRect();
                const viewportHeight = window.innerHeight;
                const viewportWidth = window.innerWidth;
                const dropdownHeight = 320; // Approximate max height (max-h-80 = 320px)
                const spaceBelow = viewportHeight - rect.bottom;
                const spaceAbove = rect.top;

                // Set dropdown width - responsive
                if (viewportWidth < 640) {
                    // Mobile: use viewport width with padding
                    this.dropdownWidth = Math.min(viewportWidth - 32, 400);
                } else {
                    // Desktop: match button width or minimum width
                    const buttonWidth = dropdownButton.offsetWidth;
                    this.dropdownWidth = Math.max(buttonWidth, 256);
                }

                // Determine position based on available space
                if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
                    this.dropdownPosition = 'top';
                } else {
                    this.dropdownPosition = 'bottom';
                }

                // Ensure dropdown doesn't overflow viewport horizontally on mobile
                if (viewportWidth < 640) {
                    const dropdownLeft = rect.left;
                    const dropdownRight = dropdownLeft + this.dropdownWidth;

                    if (dropdownRight > viewportWidth - 16) {
                        // Adjust to fit within viewport
                        this.dropdownWidth = viewportWidth - dropdownLeft - 16;
                    }
                }
            });
        },
        toggleDropdown() {
            this.isDropdownOpen = !this.isDropdownOpen;
            if (this.isDropdownOpen) {
                this.searchQuery = '';
                this.$nextTick(() => {
                    this.calculateDropdownPosition();
                    // Focus search input after dropdown opens
                    if (this.$refs.searchInputRef) {
                        this.$refs.searchInputRef.focus();
                    }
                });
            }
        },
        closeDropdown() {
            this.isDropdownOpen = false;
            this.searchQuery = '';
        },
        selectCountry(country) {
            this.selectedCountryCode = country.code;
            this.closeDropdown();
        },
        onInput(event) {
            // Allow only numbers and common phone characters
            const value = event.target.value.replace(/[^\d\s\-()]/g, '');
            this.phoneNumber = value;
        },
        onFocus() {
            // Keep dropdown closed when input is focused
            if (this.isDropdownOpen) {
                this.closeDropdown();
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.telephone-input-wrapper {
    @apply w-full;
}

.survey-tel-dropdown {
    @apply px-3 py-3 rounded-l-lg border border-r-0;
    @apply bg-white text-gray-700;
    @apply transition-all duration-200;
    @apply focus:outline-none;
    border-color: #b47c2c;
    min-width: 120px;
    cursor: pointer;
}

.survey-tel-dropdown:hover {
    background-color: #f9fafb;
}

.survey-tel-dropdown.border-b47c2c {
    border-color: #b47c2c;
    box-shadow: 0 0 0 3px rgba(180, 124, 44, 0.2);
}

.survey-tel-input-field {
    @apply flex-1 px-4 py-3 rounded-r-lg;
    @apply text-gray-900 placeholder-gray-400;
    @apply transition-all duration-200;
    border: 1px solid #b47c2c;
    background-color: #fff;
    @apply focus:outline-none;
}

.survey-tel-input-field:focus {
    border-color: #b47c2c;
    box-shadow: 0 0 0 3px rgba(180, 124, 44, 0.2);
}

.survey-tel-input-field:focus-visible {
    border-color: #b47c2c;
    box-shadow: 0 0 0 3px rgba(180, 124, 44, 0.2);
}

/* Dropdown Menu Styles */
.telephone-dropdown-menu {
    position: absolute;
    z-index: 50;
    background-color: white;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
    overflow-y: auto;
    max-height: 20rem;
}

.telephone-dropdown-menu.dropdown-bottom {
    top: 100%;
    margin-top: 0.25rem;
}

.telephone-dropdown-menu.dropdown-top {
    bottom: 100%;
    margin-bottom: 0.25rem;
}

/* Responsive adjustments */
@media (max-width: 640px) {
    .telephone-dropdown-menu {
        left: 0 !important;
        right: auto !important;
        transform: translateX(0);
    }

    .survey-tel-dropdown {
        min-width: 100px;
    }
}
</style>
